/**
 * USDA hardiness zone parsing and comparison utilities.
 *
 * Zone format: "Xa" | "Xb" where X is 2–11.
 * Garden zone (from wizard/PLZ lookup): "Zone Xb".
 * Plant zone (from DB): "Xa" | "Xb".
 *
 * Higher zone number = warmer climate.
 * Within a major zone: 'b' is warmer than 'a' (e.g. 7b > 7a).
 */

// Matches "Zone 9b", "9b", "7a", "10a", "11b" — requires sub-letter (a/b).
const ZONE_PATTERN = /^(?:Zone\s+)?([2-9]|1[01])([ab])$/i;

/**
 * Parse a zone string into its components.
 * Accepts "Zone 9b", "9b", "7a" etc.
 * Returns null for bare integers ("7"), floats ("7.0"), and invalid input.
 *
 * @param {*} input
 * @returns {{ major: number, sub: 'a'|'b' } | null}
 */
export function parseZone(input) {
  if (input == null || typeof input !== 'string') return null;
  const match = input.trim().match(ZONE_PATTERN);
  if (!match) return null;
  return { major: parseInt(match[1], 10), sub: match[2].toLowerCase() };
}

function zoneToScore({ major, sub }) {
  return major * 2 + (sub === 'b' ? 1 : 0);
}

/**
 * Compare a plant's minimum required zone with the garden's zone.
 *
 * A plant's min zone is the coldest zone it can survive. If the garden
 * is colder than that, the plant won't make it through winter.
 *
 * @param {string} plantZone  e.g. "7b" — plant's minimum required zone
 * @param {string} gardenZone e.g. "Zone 8a" — garden's USDA zone
 * @returns {'ok' | 'marginal' | 'too_cold' | 'unknown'}
 *   ok       — garden is warmer than plant's minimum; plant is safe
 *   marginal — garden is exactly at the limit; hard winters may cause damage
 *   too_cold — garden winters are too cold for the plant
 *   unknown  — one or both zones could not be parsed
 */
export function compareZones(plantZone, gardenZone) {
  const plant = parseZone(plantZone);
  const garden = parseZone(gardenZone);
  if (!plant || !garden) return 'unknown';

  const plantScore = zoneToScore(plant);
  const gardenScore = zoneToScore(garden);

  if (gardenScore > plantScore) return 'ok';
  if (gardenScore === plantScore) return 'marginal';
  return 'too_cold';
}

/**
 * Convenience boolean for filtering: is this plant winter-hardy in the garden?
 *
 * Returns true when data is missing — missing zone info is not a reason to hide
 * a plant. Only returns false when the match is definitively 'too_cold'.
 *
 * @param {{ properties?: { min_hardiness_zone?: string } }} plant
 * @param {{ hardinessZone?: string }} gardenSettings
 * @returns {boolean}
 */
export function isWinterhardyInGarden(plant, gardenSettings) {
  const gardenZone = gardenSettings?.hardinessZone;
  const plantZone = plant?.properties?.min_hardiness_zone;

  if (!gardenZone || !plantZone) return true;

  return compareZones(plantZone, gardenZone) !== 'too_cold';
}
